import { useCallback, useEffect, useRef, useState } from 'react';

export function useLiveCaptureWindow(liveSeq?: string) {
  const [captureTarget, setCaptureTarget] = useState<HTMLElement | null>(null);
  const [captureWindowError, setError] = useState('');
  const popupRef = useRef<Window | null>(null);
  const closeCapture = useCallback(() => setCaptureTarget(null), []);

  const openCapture = useCallback((source: Document) => {
    if (popupRef.current && !popupRef.current.closed) { popupRef.current.focus(); return; }
    const popup = source.defaultView?.open('', '_blank', 'popup=yes,width=1180,height=900,resizable=yes,scrollbars=yes,toolbar=no,menubar=no,location=no,status=no');
    if (!popup) { setError('캡처 창이 차단되었습니다. 팝업을 허용한 뒤 다시 눌러주세요.'); return; }
    try {
      popup.document.title = '화면 캡처 | 팬버드';
      const base = popup.document.createElement('base');
      base.href = document.baseURI;
      popup.document.head.appendChild(base);
      const viewport = popup.document.createElement('meta');
      viewport.name = 'viewport';
      viewport.content = 'width=device-width, initial-scale=1';
      popup.document.head.appendChild(viewport);
      document.querySelectorAll('link[rel="stylesheet"], style:not([data-styled])').forEach(style => {
        popup.document.head.appendChild(style.cloneNode(true));
      });
      popup.document.body.style.margin = '0';
      popupRef.current = popup;
      setCaptureTarget(popup.document.body);
      setError('');
      popup.focus();
    } catch {
      popup.close();
      setError('캡처 창을 열지 못했습니다. 다시 눌러주세요.');
    }
  }, []);

  useEffect(() => {
    if (!captureTarget) return;
    const popup = captureTarget.ownerDocument.defaultView!;
    const syncTheme = () => {
      for (const name of ['data-admin-design', 'data-theme', 'class', 'style', 'lang']) {
        const value = document.documentElement.getAttribute(name);
        if (value === null) popup.document.documentElement.removeAttribute(name);
        else popup.document.documentElement.setAttribute(name, value);
      }
    };
    syncTheme();
    // Focus after the portal mounts; mounting the editor can otherwise activate its opener.
    popup.focus();
    const observer = new MutationObserver(syncTheme);
    observer.observe(document.documentElement, { attributes: true });
    const timer = window.setInterval(() => { if (popup.closed) closeCapture(); }, 500);
    const closeOnPageLeave = () => { if (!popup.closed) popup.close(); closeCapture(); };
    popup.addEventListener('pagehide', closeCapture);
    window.addEventListener('pagehide', closeOnPageLeave);
    return () => {
      observer.disconnect();
      window.clearInterval(timer);
      popup.removeEventListener('pagehide', closeCapture);
      window.removeEventListener('pagehide', closeOnPageLeave);
      if (popupRef.current === popup) popupRef.current = null;
      if (!popup.closed) popup.close();
    };
  }, [captureTarget, closeCapture]);

  useEffect(() => { closeCapture(); }, [liveSeq, closeCapture]);
  return { captureTarget, captureWindowError, openCapture, closeCapture };
}
